import { getEnv, getRecorderEmails } from '@/constants';
import { EVENT_APP_ORG_ID_KEY } from '@/constants/authKeys';
import { getValueFromLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';

export interface RuntimeConfigSchema {
  useGCAuth?: {
    [orgId: string]: boolean;
  };
  default?: boolean;
}

export interface RuntimeFlags {
  useGCAuth: boolean;
}

const RUNTIME_CONFIG_FETCH_TIMEOUT_MS = 3000;

function resolveUseGCAuth(config: RuntimeConfigSchema, orgId: string | null): boolean {
  if (orgId && config.useGCAuth && typeof config.useGCAuth[orgId] === 'boolean') {
    return config.useGCAuth[orgId];
  }
  return config.default ?? false;
}

function getRuntimeConfigUrl(): string {
  const cdnUrl = getEnv('VUE_APP_CLOUD_FRONT_URL_v2');
  if (!cdnUrl) {
    throw new Error('CDN URL not configured');
  }
  const baseUrl = cdnUrl.endsWith('/') ? cdnUrl.slice(0, -1) : cdnUrl;
  const env = getEnv('VUE_APP_GCAUTH_ENV');
  if (!env) {
    throw new Error('Environment not configured');
  }
  const configFileName = env === 'prod' ? 'event-app-runtime-config.json' : `event-app-runtime-config-${env}.json`;
  return `${baseUrl}/${configFileName}`;
}

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store'
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function loadRuntimeConfig(): Promise<RuntimeFlags> {
  const fallbackFlags: RuntimeFlags = {
    useGCAuth: false
  };

  (globalThis as any).RUNTIME_FLAGS = fallbackFlags;

  try {
    const url = getRuntimeConfigUrl();
    const orgId = getValueFromLocalstore(EVENT_APP_ORG_ID_KEY);

    const response = await fetchWithTimeout(url, RUNTIME_CONFIG_FETCH_TIMEOUT_MS);

    if (!response.ok) {
      loggerService.error(`[RuntimeConfig] Failed to fetch config: ${response.status}, using fallbackFlags`);
      return fallbackFlags;
    }

    const text = await response.text();

    const config: RuntimeConfigSchema = JSON.parse(text);

    const resolvedFlags: RuntimeFlags = {
      useGCAuth: resolveUseGCAuth(config, orgId)
    };

    (globalThis as any).RUNTIME_FLAGS = resolvedFlags;

    return resolvedFlags;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      loggerService.error('[RuntimeConfig] Fetch timed out, using fallbackFlags');
    } else {
      loggerService.error('[RuntimeConfig] Error loading config, using fallbackFlags:', error);
    }
    return fallbackFlags;
  }
}

export function getUseGCAuth(): boolean {
  loggerService.info('globalThis.RUNTIME_FLAGS.useGCAuth', (globalThis as any).RUNTIME_FLAGS?.useGCAuth);

  const traits = getValueFromLocalstore('ajs_user_traits');
  if (traits?.email && getRecorderEmails().includes(traits.email)) {
    loggerService.info('Recorder email detected, returning false for useGCAuth', traits.email);
    (globalThis as any).RUNTIME_FLAGS.useGCAuth = false;
  }

  return (globalThis as any).RUNTIME_FLAGS?.useGCAuth ?? false;
}
